import { Commit, Dispatch } from 'vuex'
import type { GenerateResultProviderState, ModelMessage } from '@/types/msq'
import { newConfigChatSession, startStreamingAgentCompletionRequest } from '@/utils/api'

export interface ConfigAssistantState {
  workflowSubmitInFlight: boolean
}

export interface SubmitWorkflowReviewPayload {
  mode: 'current-chat' | 'new-chat'
  agentName: string
  messages: ModelMessage[]
  currentSessionId: string | null
  providerState?: GenerateResultProviderState
  onSessionResolved: (sessionId: string) => void
  onChunk: (chunk: string) => void
  onComplete: (finalContent: string) => void
  onMeta?: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) => void
  onError: (error: Error) => void
}

export const configAssistant = () => ({
  state: <ConfigAssistantState>{
    workflowSubmitInFlight: false
  },
  mutations: {
    setWorkflowSubmitInFlight(state: ConfigAssistantState, value: boolean) {
      state.workflowSubmitInFlight = value
    }
  },
  actions: {
    async submitWorkflowReview(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      payload: SubmitWorkflowReviewPayload
    ): Promise<void> {
      commit('setWorkflowSubmitInFlight', true)

      try {
        let sessionId = payload.currentSessionId
        if (payload.mode === 'current-chat') {
          if (!sessionId) {
            throw new Error('No current configuration chat session is available.')
          }
        } else {
          const newSession = await newConfigChatSession()
          if (!newSession?.id) {
            throw new Error('Failed to create a new configuration chat session.')
          }
          sessionId = newSession.id
        }

        payload.onSessionResolved(sessionId)

        let streamError: Error | null = null
        const streamHandle = startStreamingAgentCompletionRequest({
          messages: payload.messages,
          name: payload.agentName,
          sessionId,
          ...(payload.mode === 'current-chat' && payload.providerState ? { providerState: payload.providerState } : {}),
          onChunk: payload.onChunk,
          onComplete: (finalContent: string) => {
            payload.onComplete(finalContent)
          },
          onMeta: payload.onMeta,
          onError: (error: Error) => {
            streamError = error
            payload.onError(error)
          }
        })

        await streamHandle.promise

        if (streamError) {
          throw streamError
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit workflow review.'
        await dispatch('updateSnackbar', { text: message, timeout: 5000 }, { root: true })
        await dispatch('addLogEvent', { title: 'Workflow Review Submit Failed', content: message }, { root: true })
        throw error instanceof Error ? error : new Error(message)
      } finally {
        commit('setWorkflowSubmitInFlight', false)
      }
    }
  },
  getters: {
    configAssistantWorkflowSubmitInFlight: (state: ConfigAssistantState): boolean => state.workflowSubmitInFlight
  }
})
